import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useComboboxTypeahead } from "@/hooks/useComboboxTypeahead";
import { fetchBanks, type BankOption } from "@/lib/sales/banksStore";
import { cn } from "@/lib/utils";
import { Building2, Check, ChevronsUpDown, Loader2 } from "lucide-react";
import { useEffect, useMemo, useState } from "react";

type ExpenseBankSelectProps = {
  value: string;
  banks?: BankOption[];
  onChange: (id: string) => void;
  placeholder?: string;
  triggerClassName?: string;
  disabled?: boolean;
};

function itemValue(id: string): string {
  return `bank:${id}`;
}

function formatBankTypeLabel(bankType: string): string {
  const trimmed = bankType.trim();
  if (!trimmed) return "Banks";
  return trimmed
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function resolveBankValue(bank: BankOption): string {
  return String(bank.id);
}

function findSelectedBank(banks: BankOption[], value: string): BankOption | null {
  if (!value.trim()) return null;
  const val = value.trim();
  return (
    banks.find((bank) => String(bank.id) === val) ??
    banks.find(
      (bank) =>
        bank.chartOfAccountsId != null && String(bank.chartOfAccountsId) === val,
    ) ??
    banks.find((bank) => bank.name.toLowerCase() === val.toLowerCase()) ??
    null
  );
}

export function ExpenseBankSelect({
  value,
  banks: banksProp,
  onChange,
  placeholder = "Select bank",
  triggerClassName,
  disabled = false,
}: ExpenseBankSelectProps) {
  const { open, setOpen, onTriggerKeyDown, onOpenChange, commandInputProps } =
    useComboboxTypeahead(disabled);
  const [highlight, setHighlight] = useState("");
  const [fetchedBanks, setFetchedBanks] = useState<BankOption[]>([]);
  const [loading, setLoading] = useState(!banksProp);

  useEffect(() => {
    if (banksProp) return;
    let cancelled = false;
    setLoading(true);
    void fetchBanks()
      .then((rows) => {
        if (!cancelled) setFetchedBanks(rows);
      })
      .catch(() => {
        if (!cancelled) setFetchedBanks([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [banksProp]);

  const banks = banksProp ?? fetchedBanks;

  const selected = useMemo(() => findSelectedBank(banks, value), [banks, value]);

  const groups = useMemo(() => {
    const byType = new Map<string, BankOption[]>();
    for (const bank of banks) {
      const parent = formatBankTypeLabel(bank.bankType ?? "");
      const list = byType.get(parent) ?? [];
      list.push(bank);
      byType.set(parent, list);
    }
    return Array.from(byType.entries()).map(([parent, items]) => ({
      parent,
      items: [...items].sort(
        (a, b) =>
          (a.sortOrder ?? 0) - (b.sortOrder ?? 0) ||
          a.name.localeCompare(b.name, undefined, { numeric: true }),
      ),
    }));
  }, [banks]);

  const resolvedValue = selected ? resolveBankValue(selected) : value;

  useEffect(() => {
    if (!open) return;
    setHighlight(resolvedValue ? itemValue(resolvedValue) : "");
    const frame = requestAnimationFrame(() => {
      document
        .querySelector<HTMLElement>("[data-expense-bank-selected='true']")
        ?.scrollIntoView({ block: "nearest" });
    });
    return () => cancelAnimationFrame(frame);
  }, [open, resolvedValue]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next);
        if (next) setHighlight(resolvedValue ? itemValue(resolvedValue) : "");
      }}
      modal
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled || loading}
          onKeyDown={onTriggerKeyDown}
          className={cn(
            "glass-input h-11 w-full justify-between px-3.5 font-normal text-sm transition-all hover:bg-muted/30 hover:border-primary/40",
            !selected && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="flex items-center gap-2 truncate">
            {loading ? (
              <Loader2 className="h-3.5 w-3.5 shrink-0 animate-spin opacity-60 text-primary" />
            ) : (
              <Building2 className="h-3.5 w-3.5 shrink-0 opacity-60 text-primary" />
            )}
            <span className="truncate">
              {loading ? (
                "Loading banks…"
              ) : selected ? (
                <>
                  <span className="font-semibold text-foreground">{selected.name}</span>
                  {selected.code ? (
                    <span className="ml-1.5 text-xs text-muted-foreground font-normal">
                      ({selected.code})
                    </span>
                  ) : null}
                </>
              ) : (
                placeholder
              )}
            </span>
          </span>
          <ChevronsUpDown className="ml-1.5 h-3.5 w-3.5 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[var(--radix-popover-trigger-width)] min-w-[300px] p-0 z-[300] shadow-xl border-border/60"
        align="start"
        sideOffset={4}
      >
        <Command
          value={highlight}
          onValueChange={setHighlight}
          filter={(item, search, keywords) => {
            const q = search.trim().toLowerCase();
            if (!q) return 1;
            const haystack = `${item} ${(keywords ?? []).join(" ")}`.toLowerCase();
            return haystack.includes(q) ? 1 : 0;
          }}
        >
          <CommandInput
            {...commandInputProps}
            placeholder="Search bank, type or code…"
            className="h-10 text-xs border-none focus:ring-0"
          />
          <CommandList className="max-h-[260px] overflow-y-auto p-1">
            <CommandEmpty className="py-3 text-center text-xs text-muted-foreground">
              {loading ? "Loading banks…" : banks.length === 0 ? "No banks found." : "No bank found."}
            </CommandEmpty>
            {groups.map((group) => (
              <CommandGroup
                key={group.parent}
                heading={group.parent}
                className="[&_[cmdk-group-heading]]:text-[10px] [&_[cmdk-group-heading]]:font-bold [&_[cmdk-group-heading]]:uppercase [&_[cmdk-group-heading]]:tracking-wider [&_[cmdk-group-heading]]:text-primary/80 [&_[cmdk-group-heading]]:px-2 [&_[cmdk-group-heading]]:py-1.5"
              >
                {group.items.map((bank) => {
                  const val = resolveBankValue(bank);
                  const isSelected =
                    val === resolvedValue ||
                    String(bank.id) === value ||
                    (bank.chartOfAccountsId != null &&
                      String(bank.chartOfAccountsId) === value);

                  return (
                    <CommandItem
                      key={bank.id}
                      value={itemValue(val)}
                      keywords={[bank.name, bank.shortName ?? "", bank.code ?? "", bank.bankType ?? ""]}
                      data-expense-bank-selected={isSelected ? "true" : undefined}
                      onSelect={() => {
                        onChange(val);
                        setOpen(false);
                      }}
                      className={cn(
                        "text-xs flex items-center justify-between cursor-pointer rounded-md px-2.5 py-2 my-0.5 transition-colors",
                        isSelected && "bg-primary/10 font-semibold text-primary",
                      )}
                    >
                      <div className="flex items-center gap-2 min-w-0 pr-2">
                        <span className="truncate font-medium">{bank.name}</span>
                        {bank.code ? (
                          <span className="font-mono text-[10px] opacity-70 bg-muted/60 px-1.5 py-0.5 rounded border border-border/30 shrink-0">
                            #{bank.code}
                          </span>
                        ) : null}
                      </div>
                      <Check
                        className={cn(
                          "ml-2 h-3.5 w-3.5 shrink-0 text-primary",
                          isSelected ? "opacity-100" : "opacity-0",
                        )}
                      />
                    </CommandItem>
                  );
                })}
              </CommandGroup>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
